import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  makeStyles,
  TableCell,
  Table,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Button,
  ButtonGroup,
  Tooltip,
  Link,
} from "@material-ui/core";
import { Checkbox, TableBody, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import productPageStyle from "features/products/styles";
import { get, set, sequence } from "@totalsoft/change-tracking-react";
import ScheduleTableCell from "./ScheduleTableCell";
import { Autocomplete, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { PRODUCT_SCHEDULE, SCHEDULE_TYPE_CELL_PARAM_DEFINITIONS } from "features/products/queries";
import { emptyArray, emptyObject, emptyString } from "constants/defaultValues";
import { any } from "ramda";
import CellParamConfiguration from "./CellParamConfiguration";
import moment from "moment";
import ScrollbarWrapper from "features/common/components/ScrollbarWrapper";
import { GET_CELL_PARAMS_TEMPLATE_QUERY } from "features/common/queries";
import { Warning } from "@material-ui/icons";

const useStyles = makeStyles(productPageStyle);

function AdvancedEditProductScheduleCells(props) {
  const { productLens, settingsLens, quote, cellParamTemplates } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const styleProps = useMemo(() => ({ fullScreen }), [fullScreen]);
  const classes = useStyles(styleProps);
  const clientQuery = useClientQueryWithErrorHandling();

  const product = productLens |> get;
  const settings = settingsLens |> get;

  const { data: cellParamDefinitionsData, loading: loadingCellParamDefinitionsData } =
    useQueryWithErrorHandling(SCHEDULE_TYPE_CELL_PARAM_DEFINITIONS, {
      skip: !product.productId || !product.scheduleTypeId,
      variables: { productId: product.productId, scheduleTypeId: product.scheduleTypeId },
    }) || emptyObject;
  const cellParamDefinitions = cellParamDefinitionsData?.scheduleTypeCellParamDefinitions || emptyArray;

  const { data: productScheduleData, loading: loadingProductScheduleData } =
    useQueryWithErrorHandling(PRODUCT_SCHEDULE, {
      skip: !product.quoteProductId,
      variables: { quoteProductId: product.quoteProductId },
    }) || emptyObject;
  const productSchedule = productScheduleData?.quoteProduct?.productSchedule || emptyObject;

  const handleChangeCellParamDef = useCallback(
    (scheduleTypeCellParamId) => {
      const clearExistingParams = settings.cellParamConfigurations.length === 0;
      const cellParamsConfigurations = productLens.cellParamsConfigurations |> get;
      const def = cellParamDefinitions.find((x) => x.scheduleTypeCellParamId === scheduleTypeCellParamId);
      const getDefaultValue = (cellParamTypeId) => {
        switch (cellParamTypeId) {
          case -1724:
            return "false";
          case -1726:
          case -1727:
          case -1729:
          case -1829:
            return undefined;
          case -1728:
            return moment(new Date()).format("YYYY-MM-DDT00:00:00");
        }
      };
      const newParam = {
        scheduleTypeCellParamId: def.scheduleTypeCellParamId,
        scheduleTypeCellParamCode: def.cellParamName,
        value: getDefaultValue(def.cellParamTypeId),
      };

      const configsToAdd = [];
      settings.years.forEach((year) =>
        year.months.forEach((month) => {
          if (!month.selected) return;
          const existingConfig = cellParamsConfigurations.find((x) => moment(month.date).isSame(x.monthDate, "day"));
          if (existingConfig) {
            const existingParam = existingConfig.params.find(
              (p) => p.scheduleTypeCellParamId === def.scheduleTypeCellParamId
            );
            if (!existingParam) {
              if (clearExistingParams) existingConfig.params.splice(0, existingConfig.params.length);
              existingConfig.params.push(newParam);
            }
          } else {
            const newConfig = { monthDate: moment(month.date).format("YYYY-MM-DDT00:00:00"), params: [newParam] };
            configsToAdd.push(newConfig);
          }
        })
      );

      const newCellParamsConfigurations = [...cellParamsConfigurations, ...configsToAdd];
      (productLens.cellParamsConfigurations |> set)(newCellParamsConfigurations);
    },
    [
      cellParamDefinitions,
      settings.years,
      productLens.cellParamsConfigurations,
      settings?.cellParamConfigurations?.length,
    ]
  );

  const handleEditCellParamConfig = useCallback(
    (param, paramDef, value) => {
      settings.years.forEach((y) =>
        y.months.forEach((m) => {
          if (!m.selected) return;
          const configLens = (productLens.cellParamsConfigurations |> sequence).find((x) =>
            moment(m.date).isSame(x.monthDate |> get, "day")
          );
          const paramLens = (configLens.params |> sequence).find(
            (x) => (x.scheduleTypeCellParamId |> get) === param.scheduleTypeCellParamId
          );

          const stringValue =
            value != null && paramDef.cellParamTypeId === -1728
              ? moment(value).format("YYYY-MM-DDT00:00:00")
              : value?.toString();
          (paramLens.value |> set)(stringValue);
        })
      );
    },
    [settings.years, productLens.cellParamsConfigurations]
  );

  const handleClearCellParamConfig = useCallback(
    (param) => {
      settings.years.forEach((y) =>
        y.months.forEach((m) => {
          if (!m.selected) return;
          const configLens = (productLens.cellParamsConfigurations |> sequence).find((x) =>
            moment(m.date).isSame(x.monthDate |> get, "day")
          );
          const params = (configLens.params |> get).filter(
            (x) => x.scheduleTypeCellParamId !== param.scheduleTypeCellParamId
          );
          (configLens.params |> set)(params);
        })
      );
    },
    [settings.years, productLens.cellParamsConfigurations]
  );

  const handleModifyCellSelection = useCallback(
    (buttonId) => {
      (settingsLens.years |> sequence).forEach((yearLens) => {
        (yearLens.months |> sequence).forEach((monthLens) =>
          buttonId === "select-all" ? (monthLens.selected |> set)(true) : (monthLens.selected |> set)(false)
        );
      });
      if (buttonId === "clear-options") {
        (productLens.cellParamsConfigurations |> set)([]);
        (productLens.capitalPercentAutoCalculation |> set)(true);
      }
    },
    [settingsLens.years, productLens.cellParamsConfigurations, productLens.capitalPercentAutoCalculation]
  );

  const handleMonthClick = useCallback(
    (month) => () => {
      const startDate = settingsLens.firstCellStartDate |> get;
      const endDate = settingsLens.lastCellEndDate |> get;
      const removeSelection = (settingsLens.years |> sequence).some((yearLens) =>
        (yearLens.months |> sequence).some((monthLens) => {
          return (monthLens.index |> get) === month.index && (monthLens.selected |> get) === true;
        })
      );

      (settingsLens.years |> sequence).forEach((yearLens) =>
        (yearLens.months |> sequence).forEach((monthLens) => {
          if (
            (monthLens.index |> get) === month.index &&
            (monthLens.date |> get) >= startDate &&
            (monthLens.date |> get) <= endDate
          ) {
            (monthLens.selected |> set)(!removeSelection);
          }
        })
      );
    },
    [settingsLens.years, settingsLens.firstCellStartDate, settingsLens.lastCellEndDate]
  );

  const handleYearClick = useCallback(
    (year) => () => {
      const startDate = settingsLens.firstCellStartDate |> get;
      const endDate = settingsLens.lastCellEndDate |> get;

      const removeSelection = (settingsLens.years |> sequence).some(
        (yearLens) =>
          (yearLens.name |> get) === year.name &&
          (yearLens.months |> sequence).some((monthLens) => {
            return (monthLens.selected |> get) === true;
          })
      );

      (settingsLens.years |> sequence).forEach((yearLens) => {
        if ((yearLens.name |> get) === year.name)
          (yearLens.months |> sequence).forEach((monthLens) => {
            if ((monthLens.date |> get) >= startDate && (monthLens.date |> get) <= endDate) {
              (monthLens.selected |> set)(!removeSelection);
            }
          });
      });
    },
    [settingsLens.years, settingsLens.firstCellStartDate, settingsLens.lastCellEndDate]
  );

  const handleCellSelection = useCallback(
    (event) => {
      switch (event.currentTarget.id) {
        case "clear-options":
          handleModifyCellSelection(event.currentTarget.id);
          break;
        case "select-all":
          handleModifyCellSelection(event.currentTarget.id);
          break;
        case "select-none":
          handleModifyCellSelection(event.currentTarget.id);
          break;
      }
    },
    [handleModifyCellSelection]
  );

  const updateScheduleCellParamsFromTemplate = useCallback(
    async (cellParamsTemplateId) => {
      const { data } = await clientQuery(GET_CELL_PARAMS_TEMPLATE_QUERY, {
        variables: {
          cellParamsTemplateId: cellParamsTemplateId,
          productId: productLens.productId |> get,
          scheduleTypeId: productLens.scheduleTypeId |> get,
          duration: productLens.duration |> get,
          firstInstallmentDate: productLens.firstInstallmentDate |> get,
          periodicity: productLens.periodicity |> get,
        },
      });
      (productLens.cellParamsConfigurations |> set)(data?.cellParamsTemplate?.cellParamsConfigurations);
    },
    [
      clientQuery,
      productLens.productId,
      productLens.scheduleTypeId,
      productLens.duration,
      productLens.firstInstallmentDate,
      productLens.periodicity,
      productLens.cellParamsConfigurations,
    ]
  );

  const handleChangeCapitalAutoCalculation = useCallback(() => {
    (productLens.capitalPercentAutoCalculation |> set)(!(productLens.capitalPercentAutoCalculation |> get));
  }, [productLens.capitalPercentAutoCalculation]);

  const loading = loadingCellParamDefinitionsData || loadingProductScheduleData;

  return (
    <ScrollbarWrapper className={classes.scrollbar}>
      <Grid container spacing={2} direction="column" className={classes.scheduleCellsContainer}>
        <Grid
          item
          container
          spacing={2}
          alignContent="center"
          alignItems="center"
          justifyContent="space-evenly"
          className={classes.lightBorderBottom}
        >
          {cellParamDefinitions.map((item, index) => {
            return (
              <Grid item key={index} xs={true}>
                <span className={classes.marginRight}>
                  <FiberManualRecordIcon className={classes.optionDot} style={{ color: item.color }} />
                </span>
                <Typography display="inline" className={classes.fontSize12}>
                  {item.cellParamDisplayName}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
        <Grid item container alignContent="center" alignItems="center" spacing={1}>
          {cellParamTemplates?.length > 0 && (
            <Grid item xs={12} lg={5}>
              <Autocomplete
                options={cellParamTemplates}
                value={settings.template}
                onChange={updateScheduleCellParamsFromTemplate}
                valueKey={"scheduleTypeCellParamTemplateId"}
                labelKey={"templateName"}
                label={t("Product.ScheduleTemplate")}
                simpleValue
                fullWidth
                noOptionsText={t("General.AutocompleteNoOptions")}
              />
            </Grid>
          )}
          <Grid item container xs={12} lg={cellParamTemplates?.length > 0 ? 7 : 12} justifyContent="flex-end">
            <ButtonGroup variant="text">
              <Tooltip placement="top" title={t("Tooltips.ClearOptions")}>
                <Button id="clear-options" color={"default"} onClick={handleCellSelection}>
                  {t("EditProduct.ClearOptions")}
                </Button>
              </Tooltip>
              <Tooltip placement="top" title={t("Tooltips.SelectAll")}>
                <Button id="select-all" color={"default"} onClick={handleCellSelection}>
                  {t("EditProduct.SelectAll")}
                </Button>
              </Tooltip>
              <Tooltip placement="top" title={t("Tooltips.SelectNone")}>
                <Button id="select-none" color={"default"} onClick={handleCellSelection}>
                  {t("EditProduct.SelectNone")}
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Grid>
        </Grid>
        <ScrollbarWrapper>
          <Grid item>
            {loading && <LoadingFakeText lines={10} />}
            {!loading && (
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {settings.years.length > 0 &&
                      settings.years[0].months.map((month) => (
                        <TableCell key={month.index} className={classes.monthHeaderCell}>
                          <Link underline="hover" color="textPrimary" onClick={handleMonthClick(month)}>
                            <Typography variant="caption" className={classes.fontSize12WithPointer}>
                              {month.name}
                            </Typography>
                          </Link>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.years.map((year) => (
                    <TableRow key={year.name}>
                      <TableCell variant="head" className={classes.yearHeaderCell}>
                        <Link underline="hover" color="textPrimary" onClick={handleYearClick(year)}>
                          <Typography variant="caption" className={classes.fontSize12WithPointer}>
                            {year.name}
                          </Typography>
                        </Link>
                      </TableCell>
                      {year.months.map((month) => (
                        <ScheduleTableCell
                          key={month.index}
                          month={month}
                          productSchedule={productSchedule}
                          productLens={productLens}
                          quote={quote}
                          cellParamDefinitions={cellParamDefinitions}
                          settingsLens={settingsLens}
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Grid>
        </ScrollbarWrapper>
        <Grid item container className={classes.optionsContainer}>
          {!any((y) => any((m) => m.selected, y.months), settings.years) ? (
            <div className={classes.optionsBorder}>
              <Grid container direction="column" alignItems="center" justifyContent="center">
                <Grid item>
                  <Checkbox fontSize="large" color="default" disabled />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle2" className={classes.typography}>
                    {t("General.Options")}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography align="center">{t("Product.OptionMessage")}</Typography>
                </Grid>
              </Grid>
            </div>
          ) : (
            <Grid item container>
              {settings.showMixedConfiguredOptionsWarning && (
                <Grid item xs={12} container className={classes.warning}>
                  <Warning className={classes.warningIcon} />
                  <Typography variant="subtitle2" component="div">
                    {t("EditProduct.MixedOptions")}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2">{t("Product.SelectOption")}</Typography>
                <Autocomplete
                  //TO DO: !selectedOptions[option.ScheduleTypeCellParamId]
                  key={"options"}
                  label={t("General.Options")}
                  simpleValue
                  labelKey={"cellParamDisplayName"} //TO DO: show color
                  valueKey={"scheduleTypeCellParamId"}
                  options={cellParamDefinitions || emptyArray}
                  onChange={handleChangeCellParamDef}
                  helperText={emptyString}
                  noOptionsText={t("General.AutocompleteNoOptions")}
                />
              </Grid>
              <Grid item container xs={12}>
                {settings.cellParamConfigurations.map((param) => (
                  <CellParamConfiguration
                    key={param.scheduleTypeCellParamId}
                    param={param}
                    onEdit={handleEditCellParamConfig}
                    onClear={handleClearCellParamConfig}
                    cellParamDefinitions={cellParamDefinitions}
                    currencyCode={quote.currencyCode}
                    onCheckBoxChange={handleChangeCapitalAutoCalculation}
                    capitalPercentAutoCalculation={product.capitalPercentAutoCalculation}
                  />
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ScrollbarWrapper>
  );
}

AdvancedEditProductScheduleCells.propTypes = {
  quote: PropTypes.object.isRequired,
  productLens: PropTypes.object.isRequired,
  settingsLens: PropTypes.object.isRequired,
  cellParamTemplates: PropTypes.array.isRequired,
};

export default AdvancedEditProductScheduleCells;
